import { ArenaGameState, Card } from '../../../state/ArenaTypes';
import {
  GameEventSynthesizer,
  createDeterministicEventId,
  GameEventItem,
  HeartsBrokenPayload
} from '../../common/events';

/**
 * Hearts Game Event Synthesizer.
 * Synthesizes Hearts-specific domain events (such as HEARTS_BROKEN)
 * by inspecting authoritative state transitions and current trick cards.
 */
export class HeartsEventSynthesizer extends GameEventSynthesizer {
  public readonly gameType = 'HEARTS';
  private prevHeartsBroken = false;

  public override synthesize(
    currentState: ArenaGameState,
    previousState?: ArenaGameState | null
  ): GameEventItem[] {
    const isHeartsBroken = Boolean(currentState?.heartsBroken);
    const wasBroken =
      previousState !== undefined && previousState !== null
        ? Boolean(previousState.heartsBroken)
        : this.prevHeartsBroken;

    const currentHand = currentState?.handNumber ?? 0;
    const shouldTrigger = this.detectOncePerHandTransition(
      'HEARTS_BROKEN',
      isHeartsBroken,
      wasBroken,
      currentHand,
      currentState?.phase
    );

    this.prevHeartsBroken = isHeartsBroken;

    if (!shouldTrigger) {
      return [];
    }

    const currentTrickCards = (currentState?.currentTrick || [])
      .map((t) => t.card)
      .filter((c): c is Card => Boolean(c));

    const breakingCard =
      [...currentTrickCards].reverse().find(
        (c) =>
          c.suit === 'H' ||
          c.id?.startsWith('H_') ||
          c.id === 'S_Q' ||
          c.id === 'QS' ||
          (c.suit === 'S' && c.rank === 'Q')
      ) ||
      (currentState?.currentTrick && currentState.currentTrick.length > 0
        ? currentState.currentTrick[currentState.currentTrick.length - 1]?.card ?? null
        : null);

    const isQueen = Boolean(
      breakingCard?.id === 'S_Q' ||
      breakingCard?.id === 'QS' ||
      (breakingCard?.suit === 'S' && breakingCard?.rank === 'Q')
    );

    const eventId = createDeterministicEventId(this.gameType, 'HEARTS_BROKEN', currentHand);
    const payload: HeartsBrokenPayload = {
      lastCard: breakingCard ?? null,
      isQueen
    };

    const heartsBrokenEvent: GameEventItem = {
      id: eventId,
      type: 'HEARTS_BROKEN',
      payload,
      timestamp: Date.now()
    };

    return [heartsBrokenEvent];
  }

  public override resetHand(handNumber: number): void {
    super.resetHand(handNumber);
    this.prevHeartsBroken = false;
  }

  public override reset(): void {
    super.reset();
    this.prevHeartsBroken = false;
  }
}
